import { MRT_ColumnDef } from "mantine-react-table"
import { ExtendedInfinitScrollingTable } from "../../../-components/infinit-scrolling-table/extended-infinit-scrolling-table"
import {
  ContractDto,
  ContractTableRequest,
} from "../../../../../@client/Theasoft_ApiServer_Contracts"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { InfinitePersonMultiSelect } from "../../../../../-components/SelectFromQuery/InfinitePersonMultiSelect"
import { DepartmentOrWorkareaTreeSelect } from "../../../../../-components/department-workarea-tree-select"
import { ExclusiveProperties } from "../../../../../utils/exclusive-properties"
import { PaginatedTableRequest } from "../../../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { DateOnlyInput } from "../../../../../-components/date-only"
import { ContractApprovalStatusMultiSelect } from "../../../../../-components/ContractApprovalStatusMultiSelect"
import { ContractTypeTreeSelect } from "../../../../../-components/contract-type-tree-select"
import { useTranslation } from "react-i18next"
import { Page } from "../../../../../-components/Page"
import { TextInput } from "@mantine/core"
import { useNavigate } from "@tanstack/react-router"
import { RenderSimplePerson } from "@/blocks/persons-and-users/User"

export function ContractTablePage() {
  const [partialInput, setPartialInput] = React.useState<
    ExclusiveProperties<ContractTableRequest, PaginatedTableRequest>
  >({
    startDate: null,
    endDate: null,
    departmentTreeNodes: [],
    contractTypeTreeNodes: [],
    contractStatusIds: [],
    personIds: [],
    productionRoles: "",
  })

  const { t } = useTranslation("app_server")

  const columns = React.useMemo<MRT_ColumnDef<ContractDto>[]>(
    () => [
      {
        accessorKey: "contractId",
        header: "ID",
      },
      {
        accessorKey: "personId",
        header: t("Person"),
        Cell: ({ row }) => {
          const { personId } = row.original
          return <RenderSimplePerson suspense={false} personId={personId} />
        },
        Filter: () => (
          <InfinitePersonMultiSelect
            multiple
            value={partialInput.personIds.map((id) => id.toString())}
            onSelectionChange={(personIds) =>
              setPartialInput((prev) => ({
                ...prev,
                personIds: personIds.map((id) => parseInt(id)),
              }))
            }
            filterOptions={{ personCategoryTypes: ["Person"] }}
            flex={1}
            label=""
          />
        ),
      },
      {
        accessorKey: "departmentName",
        header: t("Department"),
        Filter: () => (
          <DepartmentOrWorkareaTreeSelect
            initialCheckedState={partialInput.departmentTreeNodes}
            onCheckedStateChange={(departmentTreeNodes) =>
              setPartialInput((prev) => ({ ...prev, departmentTreeNodes }))
            }
            flex={1}
            label=""
          />
        ),
      },
      {
        accessorKey: "startDate",
        header: t("Start"),
        Cell: ({ row }) => getFormattedDate(row.original.startDate),
        Filter: () => (
          <DateOnlyInput
            value={partialInput.startDate}
            maxDate={partialInput.endDate}
            onChange={(startDate) =>
              setPartialInput((prev) => ({ ...prev, startDate }))
            }
            clearable
          />
        ),
      },
      {
        accessorKey: "endDate",
        header: t("End"),
        Cell: ({ row }) => getFormattedDate(row.original.endDate),
        Filter: () => (
          <DateOnlyInput
            minDate={partialInput.startDate}
            value={partialInput.endDate}
            onChange={(endDate) =>
              setPartialInput((prev) => ({ ...prev, endDate }))
            }
            clearable
          />
        ),
      },
      {
        accessorKey: "status",
        header: t("Status"),
        Filter: () => (
          <ContractApprovalStatusMultiSelect
            flex={1}
            value={partialInput.contractStatusIds}
            onChange={(contractStatusIds) =>
              setPartialInput((prev) => ({ ...prev, contractStatusIds }))
            }
          />
        ),
      },
      {
        accessorKey: "contractTypeName",
        header: t("ContractType"),
        Filter: () => (
          <ContractTypeTreeSelect
            flex={1}
            initialCheckedState={partialInput.contractTypeTreeNodes}
            onCheckedStateChange={(contractTypeTreeNodes) =>
              setPartialInput((prev) => ({ ...prev, contractTypeTreeNodes }))
            }
          />
        ),
      },
      {
        accessorKey: "productionRoles",
        header: `${t("Production")}/${t("Role")}`,
        Cell: ({ row }) => row.original.productionRoles.join(", "),
        Filter: () => (
          <TextInput
            flex={1}
            value={partialInput.productionRoles}
            onChange={(e) =>
              setPartialInput((prev) => ({
                ...prev,
                productionRoles: e.target.value,
              }))
            }
          />
        ),
      },
    ],
    [partialInput, t]
  )
  const navigate = useNavigate()

  return (
    <Page>
      <ExtendedInfinitScrollingTable
        url="/api/Contracts/GetContractTable"
        columnDefinitions={columns}
        defaultSorting={[{ id: "contractId", desc: false }]}
        partialInput={partialInput}
        onClickRow={(row) =>
          navigate({
            to: "/app/contracts/contracts/$id",
            params: { id: row.contractId.toString() },
          })
        }
      />
    </Page>
  )
}
